// After a deploy an open tab can ask for a lazy chunk that no longer exists (Caddy answers 404 for a missing /assets/* file).
// The app reloads once to get the new build; a sessionStorage guard makes sure it never loops, and when the reload did not
// help the router's error state is shown. The guard is cleared once a load has run cleanly for a while, so a later deploy
// in the same tab recovers the same way.
import { ErrorState } from "@offsilo/ui/state";
import { useEffect, useState } from "react";
import { m } from "@/paraglide/messages.js";

const KEY = "os-chunk-reload";
const SETTLED_MS = 10_000;

type GuardStore = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** A failed dynamic import or module script, as Chromium, Firefox and Safari word it. */
export function isChunkError(error: unknown): boolean {
  const text = error instanceof Error ? `${error.name} ${error.message}` : String(error ?? "");
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError/i.test(text);
}

/** Reloads unless this session already reloaded for a chunk. True when it reloaded. No storage: never reloads. */
export function reloadOnceForChunk(store: GuardStore | null, reload: () => void): boolean {
  try {
    if (!store || store.getItem(KEY)) return false;
    store.setItem(KEY, String(Date.now()));
  } catch {
    return false;
  }
  reload();
  return true;
}

function sessionStore(): GuardStore | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Call once at start-up: catches Vite's preload errors and unhandled chunk failures, and clears the guard after a clean load. */
export function installChunkReload(): void {
  if (typeof window === "undefined") return;
  const reload = () => window.location.reload();
  window.addEventListener("vite:preloadError", (e) => {
    if (reloadOnceForChunk(sessionStore(), reload)) e.preventDefault();
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (isChunkError(e.reason) && reloadOnceForChunk(sessionStore(), reload)) e.preventDefault();
  });
  window.setTimeout(() => {
    try {
      sessionStore()?.removeItem(KEY);
    } catch {}
  }, SETTLED_MS);
}

/** The router's default error component: a failed lazy route import reloads once; anything else, or a second failure, shows the error state. */
export function RouteError({ error, reset }: { error: unknown; reset: () => void }) {
  const [reloading] = useState(() => isChunkError(error) && reloadOnceForChunk(sessionStore(), () => window.location.reload()));
  useEffect(() => {
    if (!reloading) console.error(error);
  }, [error, reloading]);
  if (reloading) return null;
  return <ErrorState title={m.error_title()} body={m.error_body()} retryLabel={m.action_retry()} onRetry={reset} />;
}
